<script setup name="UIDAdjustContours">
import { CurveInterpolator } from 'curve-interpolator';
import * as d3 from 'd3';

import { computed, onMounted, ref, toRefs, watch } from 'vue';

import { nudgePoints2D } from '@/src/core/contours/nudge';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import * as ContourDefinitions from '@/src/store/contours/contour-definitions';
import { encodeContourLabelItemId } from '@/src/store/contours/contour-definitions';

import {
  convertArrayToPointArray,
  convertPointArrayToArray,
} from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import {
  getClosestPointArrayIdx,
  getClosestPointIdx,
} from '@/src/store/contours/contour-processing/getClosestPointIdx';
import {
  ViewportDrawingMode,
  ViewportToolMode,
} from '@/src/store/contours/types/tool-modes-type';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import {
  CONTOUR_STROKE,
  ENDPOINT_RADIUS,
} from './contour-draw-constants';
import { getLineGenerator } from './contour-draw-geometry';
import {
  appendArrow,
  appendCircle,
  appendPath,
  applyGroupTransform,
} from './contour-svg-primitives';
import { coordinateTransformation } from './coordinateTransformation';
import useEventBus from './eventBus';

import {
  drawRangeCrossbars,
  getLocalizerDir2D,
  RANGE_CROSS_OPACITY,
  RANGE_CROSS_STROKE,
} from './lax-range-crossbar';
import PieMenu from './PieMenu.vue';
import TextAnnotationInput from './TextAnnotationInput.vue';
import { ThresholdSegmentation } from './ThresholdSegmentation';

defineOptions({
  name: 'UIDAdjustContours',
});
const props = defineProps({
  id: { type: String, required: true },
  active: { type: Boolean, required: true },
  currentImageUid: { type: String, required: true },
  showContours: { type: Boolean, required: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});
// Add emits:
// TODO: figure out why when annotation is committed, hover state is not re-enabled. As a work around, forcing for now.
const emit = defineEmits(['forceHover']);
const DISTANCE_CONTOUR_SELECTION_THRESHOLD = 1.25;
const DISTANCE_CENTROID_SELECTION_THRESHOLD = 20;
const DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD = 15;
const DISTANCE_POINT_SELECTION_THRESHOLD = 15;
const MIN_SELECTION_CLICK_TRAVEL_DISTANCE = 2;
const THRESHOLD_SEGMENTATION_INNER_REGIONS = true;

// const viewSliceStore = useViewSliceStore();
const currentStudyStore = useCurrentStudyStore();
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();

// handle props
const {
  id: viewID,
  active,
  currentImageUid: curImageID,
  canvasWidth,
  canvasHeight,
} = toRefs(props);

// coordinate transformations composable
const {
  scaleFactor,
  imageToScreen,
  screenToImage,
  getCurrentCoordinates,
  mouseEventToScreen,
  updateLeftCornerCoords,
  imageToScreenMatrix,
  overlayTransform,
} = coordinateTransformation({
  currentImageUid: curImageID,
  id: viewID,
  canvasWidth,
  canvasHeight,
});

// helper functions

// ui
const annotationInputVisible = ref(false);
const annotationInputPosition = ref({ x: 0, y: 0 });
const annotationInputValue = ref(''); // Can be used in the future to enable text editing, but for now user can just delete and add new.
const annotationInputRef = ref(null);
const pendingAnnotationContour = ref(null);

// const currentPointer = ref('default');
const showContextMenu = ref(false);
// const targetContext = ref(null);
const contextMenuCoords = ref([0, 0]);
// const clipboard = ref([]); // Clipboard:

const currentDrawingContours = computed(
  () => contourContainerStore.getDrawingContours(curImageID.value) || null,
);
const selectedContour = computed(() =>
  viewportStateStore.getSelectedContour(viewID.value),
);

const currentDrawingContourLabel = computed(() =>
  viewportStateStore.getDrawingContour(viewID.value),
);
const currentDrawingContourProps = computed(() => {
  const properties
    = ContourDefinitions.ContourDefinitions[currentDrawingContourLabel.value];
  return properties ? { ...properties } : null;
});

const localizerDir2D = computed(() =>
  getLocalizerDir2D(viewID.value, curImageID.value, viewportStateStore, currentStudyStore),
);

// ContextMenu
const overlayContainer = ref(null);
function initContextMenu(e) {
  if (!overlayContainer.value) {
    console.warn('Overlay container not yet available');
    return;
  }

  const rect = overlayContainer.value.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  contextMenuCoords.value = [x, y];
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuCoords.value = [0, 0];
}

// ToDo review
const { onEvent } = useEventBus();

function showAnnotationInput({ event, contourLine, label, id }) {
  const inputWidth = 160;
  if (!overlayContainer.value) return;

  const rect = overlayContainer.value.getBoundingClientRect();
  let x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  // x is now the CENTER of the (translate(-50%,-50%)) input
  const half = inputWidth / 2;
  x = Math.min(Math.max(x, half), rect.width - half);

  annotationInputPosition.value = { x, y };
  pendingAnnotationContour.value = { contourLine, label, id };
  annotationInputVisible.value = true;
}

function hideAnnotationInput() {
  annotationInputVisible.value = false;
  pendingAnnotationContour.value = null;
}

function forceAnnotationCommit() {
  annotationInputRef.value?.commitAnnotationInput();
}

function commitAnnotationInput(userInputText) {
  if (!annotationInputVisible.value || !pendingAnnotationContour.value) {
    cancelAnnotationInput();

    return;
  }
  const { contourLine, label, id } = pendingAnnotationContour.value;

  const text = userInputText?.trim();
  if (text && text.length > 0) {
    contourContainerStore.addContour(
      curImageID.value,
      convertArrayToPointArray(contourLine),
      label,
      id,
      true, // triggerEvent
      true,
      false,
      text,
    );
  }

  cancelAnnotationInput();
}

function cancelAnnotationInput() {
  hideAnnotationInput();
  stopDrawing();
  // Wait 100ms before re-enabling hover to avoid immediate hover state
  setTimeout(() => {
    emit('forceHover', true);
  }, 100);

  resetMouseStates();
  resetDrawingCanvas();
  viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Pan);
}

function calculateDistance(point1, point2) {
  return Math.sqrt((point1[0] - point2[0]) ** 2 + (point1[1] - point2[1]) ** 2);
}

function calculateDistancePoints(point1, point2) {
  return Math.sqrt((point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2);
}

function equalPoints(point1, point2) {
  return point1[0] === point2[0] && point1[1] === point2[1];
}

function pointToPolylineDistance(point, polyline, targetDistance = null) {
  if (!polyline || polyline.length === 0) return Infinity;
  if (polyline.length === 1) {
    // Single point - just return distance to that point
    return Math.hypot(point[0] - polyline[0][0], point[1] - polyline[0][1]);
  }

  let minDistance = Infinity;
  const px = point[0];
  const py = point[1];

  for (let i = 0; i < polyline.length - 1; i++) {
    const x1 = polyline[i][0];
    const y1 = polyline[i][1];
    const x2 = polyline[i + 1][0];
    const y2 = polyline[i + 1][1];

    // Vector from segment start to end
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segmentLengthSq = dx * dx + dy * dy;

    let distance;

    if (segmentLengthSq === 0) {
      // Segment is a point
      distance = Math.hypot(px - x1, py - y1);
    }
    else {
      // Project point onto the line segment, clamped to [0, 1]
      const t = Math.max(
        0,
        Math.min(1, ((px - x1) * dx + (py - y1) * dy) / segmentLengthSq),
      );

      // Find the closest point on the segment
      const closestX = x1 + t * dx;
      const closestY = y1 + t * dy;

      distance = Math.hypot(px - closestX, py - closestY);
    }

    if (distance < minDistance) {
      minDistance = distance;
      // Early exit if we're within target distance
      if (
        (targetDistance !== null && minDistance < targetDistance)
        || minDistance < 1
      ) {
        return minDistance;
      }
    }
  }

  return minDistance;
}

// mouse event handling states
function isOverContourLine(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;
  const currentPos = getCurrentCoordinates(payload);

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      const contourLine = contour.getPolygonScaled();

      if (!contourLine.length) continue; // Skip empty contours
      if (contour.getProps().userEditable === false) {
        continue;
      }

      let scaledContour = convertPointArrayToArray(contourLine);
      if (scaledContour.length === 1) {
        scaledContour = scaledContour.concat(scaledContour);
      }
      const distance = pointToPolylineDistance(
        currentPos,
        scaledContour,
        DISTANCE_CONTOUR_SELECTION_THRESHOLD,
      );

      if (distance < DISTANCE_CONTOUR_SELECTION_THRESHOLD) {
        result = contour.getProps();
        return result;
      }
    }
  }
  return result;
}

function isOverSelectedContourCentroid(payload) {
  let result = null;
  if (!selectedContour.value || !currentDrawingContours.value) return result;

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (
        id !== selectedContour.value.id
        || label !== selectedContour.value.label
      ) {
        continue;
      }

      const centroid = contour.getCentroidScaled();
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);
      const distance = calculateDistancePoints(centroid, {
        x: currentPosX,
        y: currentPosY,
      });

      // Check if point is close enough to centroid
      if (
        distance * scaleFactor.value
        < DISTANCE_CENTROID_SELECTION_THRESHOLD
      ) {
        result = contour.getProps();
        return result; // Exit after finding first match
      }
    }
  }
  return result;
}

function isOverLineEndPoint(payload) {
  const result = { properties: null, staticPointIndex: 0 };
  if (!currentDrawingContours.value) return result;

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      const contourType = contour.getProps().type;
      if (
        contourType !== ContourDefinitions.ContourType.LineContour
        && contourType !== ContourDefinitions.ContourType.AnnotationArrowContour
      ) {
        continue;
      }

      if (contour.getProps().userEditable === false) {
        continue;
      }

      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);
      const contourLinePolygon = contour.getPolygonScaled();
      const scaledContour = convertPointArrayToArray(contourLinePolygon);

      const distanceFirstLinePoint = calculateDistance(scaledContour[0], [
        currentPosX,
        currentPosY,
      ]);
      const distanceSecondLinePoint = calculateDistance(scaledContour[1], [
        currentPosX,
        currentPosY,
      ]);

      if (
        distanceFirstLinePoint * scaleFactor.value
        < DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD
        || distanceSecondLinePoint * scaleFactor.value
        < DISTANCE_LINE_ENDPOINT_SELECTION_THRESHOLD
      ) {
        if (distanceFirstLinePoint < distanceSecondLinePoint) {
          result.staticPointIndex = 1;
        }
        result.properties = contour.getProps();

        return result; // Exit after finding first match
      }
    }
  }
  return result;
}

function isOverPointContour(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (
        contour.getProps().type !== ContourDefinitions.ContourType.PointContour
      )
        continue;
      if (contour.getProps().userEditable === false) {
        continue;
      }

      const point = contour.getPolygonScaled()[0];
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);

      const distance = calculateDistancePoints(
        { x: currentPosX, y: currentPosY },
        point,
      );
      if (distance * scaleFactor.value < DISTANCE_POINT_SELECTION_THRESHOLD) {
        result = contour.getProps();
        return result;
      }
    }
  }
  return result;
}

function isOverTextContour(payload) {
  let result = null;
  if (!currentDrawingContours.value) return result;

  for (const [label, ids] of Object.entries(currentDrawingContours.value)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (
        contour.getProps().type
        !== ContourDefinitions.ContourType.AnnotationTextContour
      ) {
        continue;
      }
      if (contour.getProps().userEditable === false) {
        continue;
      }

      const point = contour.getPolygonScaled()[0];
      const [currentPosX, currentPosY] = getCurrentCoordinates(payload);

      const distance = calculateDistancePoints(
        { x: currentPosX, y: currentPosY },
        point,
      );
      if (
        distance * scaleFactor.value
        < DISTANCE_POINT_SELECTION_THRESHOLD * 3
      ) {
        result = contour.getProps();
        return result;
      }
    }
  }
  return result;
}

function isClosedPolygonDrawing() {
  if (!currentDrawingContourProps.value) return false;
  const isClosedPolygon
    = currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonClosedContour;
  const modifyContour
    = selectedContour.value.label
      && selectedContour.value.id !== encodeContourLabelItemId([-1]);

  return isClosedPolygon && !modifyContour;
}

function getThresholdInnerContourProbs() {
  if (!THRESHOLD_SEGMENTATION_INNER_REGIONS) {
    return null;
  }
  if (
    currentDrawingContourProps.value.label
    === ContourDefinitions.ContourLabel.SaxLvEndo
  ) {
    return ContourDefinitions.ContourDefinitions.SaxLvPapillary;
  }
  if (
    currentDrawingContourProps.value.label
    === ContourDefinitions.ContourLabel.SaxRvEndo
  ) {
    return ContourDefinitions.ContourDefinitions.SaxRvPapillary;
  }
  return null;
}

function initNudgeTool() {
  if (
    selectedContour.value
    && (currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonClosedContour
      || currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PolygonOpenContour)
  ) {
    const contourPolygon = contourContainerStore.getContour(curImageID.value, {
      label: selectedContour.value.label,
      id: selectedContour.value.id,
    });
    if (!contourPolygon || !contourPolygon.length) {
      nudgePolygon.value = [];
      nudgeRadius.value = 0;
      return;
    }

    nudgePolygon.value = contourPolygon;

    const [startPointX, startPointY] = screenToImage(
      mousePositionButtonDown.value[0],
      mousePositionButtonDown.value[1],
    );
    const startPoint = {
      x: startPointX,
      y: startPointY,
    };

    const closestPointIdx = getClosestPointIdx(startPoint, contourPolygon);
    const closestPoint = {
      x: contourPolygon[closestPointIdx].x,
      y: contourPolygon[closestPointIdx].y,
    };
    nudgeRadius.value = calculateDistancePoints(closestPoint, startPoint);
  }
  else {
    nudgeRadius.value = 0;
  }
}

// mouse event handling states
const mouseLeftButtonDown = ref(false);
const mouseRightButtonDown = ref(false);
const mouseMiddleButtonDown = ref(false);

const mousePositionButtonDown = ref(null);
const mousePathButtonDown = ref([]);
const mousePathButtonDownThreshold = ref([]);
const contourProbsButtonDown = ref(null);
const lineEndPointProbsButtonDown = ref(null);
const centroidContourProbsButtonDown = ref(null);
const pointContourProbsButtonDown = ref(null);

const movingPointProbs = ref(null);

const mouseActionDrawContour = ref(false);
const mouseActionMoveLineEndPoint = ref(false);
const mouseActionMoveCentroid = ref(false);
const mouseActionMovePoint = ref(false);
const mouseActionSkipPointDraw = ref(false);
const mouseActionNudgeTool = ref(false);
const mouseActionThreshold = ref(false);
const thresholdSegmentation = ref(null);
const nudgeRadius = ref(0);
const nudgePolygon = ref([]);
function resetMouseStates() {
  mouseLeftButtonDown.value = false;
  mouseRightButtonDown.value = false;
  mouseMiddleButtonDown.value = false;

  mousePositionButtonDown.value = null;
  mousePathButtonDown.value = [];
  mousePathButtonDownThreshold.value = [];
  contourProbsButtonDown.value = null;
  lineEndPointProbsButtonDown.value = { properties: null, staticPointIndex: 0 };
  centroidContourProbsButtonDown.value = null;
  pointContourProbsButtonDown.value = null;
  movingPointProbs.value = null;

  mouseActionDrawContour.value = false;
  mouseActionMoveLineEndPoint.value = false;
  mouseActionMoveCentroid.value = false;
  mouseActionMovePoint.value = false;
  mouseActionSkipPointDraw.value = false;
  mouseActionThreshold.value = false;
  mouseActionNudgeTool.value = false;
  viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
  viewportStateStore.setHideDrawingContour(viewID.value, null, '');
  viewportStateStore.setHideDrawingContourLabel(viewID.value, null);
  thresholdSegmentation.value = null;
  nudgeRadius.value = 0;
  nudgePolygon.value = [];
}

function onMouseDown(payload) {
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour)
    return;

  if (
    viewportStateStore.getDrawingMode(viewID.value)
    === ViewportDrawingMode.PointClick
    && mouseActionDrawContour.value
  ) {
    mousePositionButtonDown.value = mouseEventToScreen(payload);
    mousePathButtonDown.value.push(mousePositionButtonDown.value);
    return;
  }

  resetMouseStates();
  mouseLeftButtonDown.value = payload.button === 0;
  mouseRightButtonDown.value = payload.button === 2;
  mouseMiddleButtonDown.value = payload.button === 1;

  mousePositionButtonDown.value = mouseEventToScreen(payload);
  mousePathButtonDown.value.push(mousePositionButtonDown.value);
  contourProbsButtonDown.value = isOverContourLine(payload);
  lineEndPointProbsButtonDown.value = isOverLineEndPoint(payload);
  centroidContourProbsButtonDown.value = isOverSelectedContourCentroid(payload);
  pointContourProbsButtonDown.value = isOverPointContour(payload);
  const textContourProbsButtonDown = isOverTextContour(payload);

  if (mouseLeftButtonDown.value) {
    if (!currentDrawingContourProps.value) {
      resetMouseStates();
      return;
    }

    mouseActionDrawContour.value = true;

    // only one point to draw
    if (
      currentDrawingContourProps.value.type
      === ContourDefinitions.ContourType.PointContour
      && contourContainerStore.isPresentContourLabel(
        curImageID.value,
        currentDrawingContourLabel.value,
      )
    ) {
      mouseActionSkipPointDraw.value = true;
    }

    // check if on line end point
    if (lineEndPointProbsButtonDown.value.properties) {
      mouseActionDrawContour.value = false;
      mouseActionMoveLineEndPoint.value = true;

      viewportStateStore.setHideDrawingContour(
        viewID.value,
        lineEndPointProbsButtonDown.value.properties.label,
        lineEndPointProbsButtonDown.value.properties.id,
      );

      const line
        = currentDrawingContours.value[
          lineEndPointProbsButtonDown.value.properties.label
        ][lineEndPointProbsButtonDown.value.properties.id];

      if (line) {
        const staticPoint
          = line.getPolygonScaled()[
            lineEndPointProbsButtonDown.value.staticPointIndex
          ];
        if (staticPoint) {
          mousePathButtonDown.value.unshift(
            imageToScreen(staticPoint.x, staticPoint.y),
          );
        }
      }
      viewportStateStore.setDrawingContour(
        viewID.value,
        lineEndPointProbsButtonDown.value.properties.label,
      );
      drawEndpointMovePreview();
    }

    // check if on centroid
    // check if on centroid
    if (
      centroidContourProbsButtonDown.value
      && centroidContourProbsButtonDown.value.type
      !== ContourDefinitions.ContourType.PointContour
      && centroidContourProbsButtonDown.value.type
      !== ContourDefinitions.ContourType.AnnotationTextContour
    ) {
      mouseActionDrawContour.value = false;
      mouseActionMoveCentroid.value = true;
    }

    if (textContourProbsButtonDown) {
      mouseActionDrawContour.value = false;
      mouseActionMovePoint.value = true;
      movingPointProbs.value = {
        label: textContourProbsButtonDown.label,
        id: textContourProbsButtonDown.id,
      };
      viewportStateStore.setDrawingContour(
        viewID.value,
        textContourProbsButtonDown.label,
      );
      viewportStateStore.setSelectedContour(
        viewID.value,
        textContourProbsButtonDown.label,
        textContourProbsButtonDown.id,
      );
    }

    // check if on point
    if (pointContourProbsButtonDown.value) {
      mouseActionDrawContour.value = false;
      mouseActionMovePoint.value = true;
      movingPointProbs.value = {
        label: pointContourProbsButtonDown.value.label,
        id: pointContourProbsButtonDown.value.id,
      };
      viewportStateStore.setDrawingContour(
        viewID.value,
        pointContourProbsButtonDown.value.label,
      );
      viewportStateStore.setSelectedContour(
        viewID.value,
        pointContourProbsButtonDown.value.label,
        pointContourProbsButtonDown.value.id,
      );
    }

    if (mouseActionDrawContour.value) {
      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Threshold
      ) {
        if (
          currentDrawingContourProps.value.type
          !== ContourDefinitions.ContourType.PolygonClosedContour
        ) {
          resetMouseStates();
          return;
        }
        mouseActionThreshold.value = true;
        const image = currentStudyStore.getVtkImageData(curImageID.value);
        thresholdSegmentation.value = new ThresholdSegmentation(
          image,
          screenToImage(
            mousePositionButtonDown.value[0],
            mousePositionButtonDown.value[1],
          ),
        );

        const innerContourProbs = getThresholdInnerContourProbs();
        if (innerContourProbs) {
          viewportStateStore.setHideDrawingContourLabel(
            viewID.value,
            innerContourProbs.label,
          );
        }

        if (selectedContour.value) {
          viewportStateStore.setHideDrawingContour(
            viewID.value,
            selectedContour.value.label,
            selectedContour.value.id,
          );
        }
        return;
      }

      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        if (!selectedContour.value) {
          resetMouseStates();
          return;
        }
        mouseActionNudgeTool.value = true;
        initNudgeTool();
        viewportStateStore.setHideDrawingContour(
          viewID.value,
          selectedContour.value.label,
          selectedContour.value.id,
        );
        drawNudgeTool();
      }
    }
  } // end left
}

function onMouseMove(payload) {
  if (!active.value) return;
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour)
    return;
  if (!currentDrawingContourProps.value) return;

  if (mouseLeftButtonDown.value) {
    viewportStateStore.setDrawingActive(viewID.value, true);

    if (mouseActionDrawContour.value) {
      // freehand
      if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Freehand
      ) {
        mousePathButtonDown.value.push(mouseEventToScreen(payload));
      }
      // point click
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.PointClick
      ) {
        if (
          (currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.LineContour
            || currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.AnnotationArrowContour)
          && mousePathButtonDown.value.length === 1
        ) {
          mousePathButtonDown.value.push(mouseEventToScreen(payload));
        }
        else {
          mousePathButtonDown.value[mousePathButtonDown.value.length - 1]
            = mouseEventToScreen(payload);
        }
      }
      // threshold
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Threshold
      ) {
        if (!mouseActionThreshold.value || !thresholdSegmentation.value) return;
        const eventCoords = mouseEventToScreen(payload);
        const diffX = eventCoords[0] - mousePositionButtonDown.value[0];
        const diffY = eventCoords[1] - mousePositionButtonDown.value[1];
        thresholdSegmentation.value.setThresholdsChange(
          diffX / canvasWidth.value,
          diffY / canvasHeight.value,
        );

        mousePathButtonDownThreshold.value
          = thresholdSegmentation.value.getContours();
        drawContourPathThreshold();
        return;
      }
      // nudge
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        const point = mouseEventToScreen(payload);
        mousePathButtonDown.value.push(point);

        const [centerX, centerY] = screenToImage(point[0], point[1]);
        nudgePolygon.value = convertArrayToPointArray(
          nudgePoints2D(convertPointArrayToArray(nudgePolygon.value), {
            center: [centerX, centerY],
            radius: nudgeRadius.value,
          }),
        );
      }
      // none
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.None
      ) {
        mousePathButtonDown.value.push(mouseEventToScreen(payload));
      }

      // draw
      if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.LineContour
      ) {
        drawLine();
      }
      else if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.AnnotationArrowContour
      ) {
        drawArrow();
      }
      else if (
        currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.PointContour
        || currentDrawingContourProps.value.type
        === ContourDefinitions.ContourType.AnnotationTextContour
      ) {
        // drawPoint();
      }
      else if (
        viewportStateStore.getDrawingMode(viewID.value)
        === ViewportDrawingMode.Nudge
      ) {
        drawNudgeTool();
      }
      else {
        drawContourPath();
      }
    }

    if (mouseActionMoveLineEndPoint.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      drawEndpointMovePreview();
    }

    if (mouseActionMoveCentroid.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];
      viewportStateStore.setMoveContour(
        viewID.value,
        centroidContourProbsButtonDown.value.label,
        centroidContourProbsButtonDown.value.id,
        distancePoint,
      );
    }

    if (mouseActionMovePoint.value && movingPointProbs.value) {
      mousePathButtonDown.value.push(mouseEventToScreen(payload));
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];
      viewportStateStore.setMoveContour(
        viewID.value,
        movingPointProbs.value.label,
        movingPointProbs.value.id,
        distancePoint,
      );
    }
  } // end left
}

function stopDrawing() {
  resetMouseStates();
  viewportStateStore.setDrawingActive(viewID.value, false);

  // Defer removing the live SVG until the VTK actor has painted
  // (createImageActors runs on a watcher tick, then renderLater paints on rAF).
  // Removing only the captured node avoids wiping a freshly started drawing.
  const staleSvgNode = svg.value?.node?.();
  svg.value = null;
  if (staleSvgNode) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => staleSvgNode.remove()),
    );
  }
  else {
    resetDrawingCanvas();
  }
}

function onMouseUp(payload) {
  if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour)
    return;

  mousePathButtonDown.value.push(mouseEventToScreen(payload));
  const travelDistance = calculateDistance(
    mousePathButtonDown.value[0],
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
  );

  if (mouseLeftButtonDown.value) {
    if (mouseActionDrawContour.value) {
      let selection = false;
      // detect click
      if (travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
        if (contourProbsButtonDown.value) {
          if (
            viewportStateStore.getDrawingMode(viewID.value)
            !== ViewportDrawingMode.PointClick
            && !ContourDefinitions.equalContourIds(
              selectedContour.value,
              contourProbsButtonDown.value,
            )
          ) {
            selection = true;
            viewportStateStore.setSelectedContour(
              viewID.value,
              contourProbsButtonDown.value.label,
              contourProbsButtonDown.value.id,
            );
            viewportStateStore.setDrawingContour(
              viewID.value,
              contourProbsButtonDown.value.label,
            );
          }
        }
        else {
          if (
            viewportStateStore.getDrawingMode(viewID.value)
            !== ViewportDrawingMode.PointClick
          ) {
            if (selectedContour.value) {
              viewportStateStore.setSelectedContour(viewID.value, null, null);
            }
            else {
              viewportStateStore.setDrawingContour(viewID.value, null);
              viewportStateStore.setToolMode(
                viewID.value,
                ViewportToolMode.Pan,
              );
              stopDrawing();
              return;
            }
          }
        }
        if (selection) {
          stopDrawing();
          return;
        }
      }

      if (!mouseActionSkipPointDraw.value) {
        // point click
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.PointClick
          && (currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.PolygonClosedContour
            || currentDrawingContourProps.value.type
            === ContourDefinitions.ContourType.PolygonOpenContour)
        ) {
          if (mousePathButtonDown.value.length > 2) {
            // Get all elements except the second-to-last one
            const newArray = [
              ...mousePathButtonDown.value.slice(
                0,
                mousePathButtonDown.value.length - 2,
              ),
              mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
            ];
            mousePathButtonDown.value = newArray;
          }
          return;
        }

        // threshold
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.Threshold
        ) {
          if (travelDistance > MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
            const innerContourProbs = getThresholdInnerContourProbs();
            // remove all inner contours
            if (innerContourProbs) {
              contourContainerStore.deleteAllContoursOfLabel(
                curImageID.value,
                innerContourProbs.label,
                false,
              );
            }

            for (const [
              idx,
              contour,
            ] of mousePathButtonDownThreshold.value.entries()) {
              if (contour.length < 3) {
                // if outer contour not valid break
                if (idx === 0) {
                  stopDrawing();
                  return;
                }

                continue;
              }

              let contourLabel = currentDrawingContourLabel.value;
              if (idx > 0 && !innerContourProbs) {
                stopDrawing();
                return;
              }

              if (idx > 0) {
                contourLabel = innerContourProbs.label;
              }

              let id = encodeContourLabelItemId([-1]);
              if (
                selectedContour.value
                && selectedContour.value.label === contourLabel
              ) {
                id = selectedContour.value.id;
              }
              contourContainerStore.addContour(
                curImageID.value,
                convertArrayToPointArray(contour),
                contourLabel,
                id,
                true, // triggerEvent
                true,
                true,
              );
            }
            stopDrawing();
            return;
          }
        }

        // nudge tool
        if (
          viewportStateStore.getDrawingMode(viewID.value)
          === ViewportDrawingMode.Nudge
        ) {
          contourContainerStore.addContour(
            curImageID.value,
            nudgePolygon.value,
            selectedContour.value.label,
            selectedContour.value.id,
            true, // triggerEvent
            true,
            true,
          );

          stopDrawing();
          return;
        }

        // freehand
        let contourLine = mousePathButtonDown.value;
        if (
          currentDrawingContourProps.value?.type
          === ContourDefinitions.ContourType.LineContour
          || currentDrawingContourProps.value?.type
          === ContourDefinitions.ContourType.AnnotationArrowContour
        ) {
          contourLine = [contourLine[0], contourLine[contourLine.length - 1]];
        }
        else if (
          currentDrawingContourProps.value?.type
          === ContourDefinitions.ContourType.PointContour
          || currentDrawingContourProps.value?.type
          === ContourDefinitions.ContourType.AnnotationTextContour
        ) {
          contourLine = [contourLine[contourLine.length - 1]];
        }

        // polygon
        contourLine = contourLine.map(point =>
          screenToImage(point[0], point[1]),
        );

        let id = encodeContourLabelItemId([-1]);
        if (selectedContour.value && selectedContour.value.label) {
          id = selectedContour.value.id;
        }
        if (
          currentDrawingContourProps.value?.type
          === ContourDefinitions.ContourType.AnnotationTextContour
        ) {
          showAnnotationInput({
            event: payload,
            contourLine,
            label: currentDrawingContourLabel.value,
            id,
          });
          return;
        }

        contourContainerStore.addContour(
          curImageID.value,
          convertArrayToPointArray(contourLine),
          currentDrawingContourLabel.value,
          id,
        );
      }
    }

    if (mouseActionMoveLineEndPoint.value) {
      let contourLine = mousePathButtonDown.value;
      contourLine = [contourLine[0], contourLine[contourLine.length - 1]];
      contourLine = contourLine.map(point =>
        screenToImage(point[0], point[1]),
      );

      contourContainerStore.addContour(
        curImageID.value,
        convertArrayToPointArray(contourLine),
        lineEndPointProbsButtonDown.value.properties.label,
        lineEndPointProbsButtonDown.value.properties.id,
      );
      viewportStateStore.setHideDrawingContour(viewID.value, null, '');
    }

    if (
      mouseActionMoveCentroid.value
      && currentDrawingContourProps.value.type
      !== ContourDefinitions.ContourType.PointContour
    ) {
      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];

      contourContainerStore.translateContour(
        curImageID.value,
        {
          label: centroidContourProbsButtonDown.value.label,
          id: centroidContourProbsButtonDown.value.id,
        },
        distancePoint,
      );
      viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
    }

    if (mouseActionMovePoint.value && movingPointProbs.value) {
      if (travelDistance < MIN_SELECTION_CLICK_TRAVEL_DISTANCE) {
        if (
          movingPointProbs.value.label
          !== ContourDefinitions.ContourLabel.LaxLvEndoBasalPoint
        ) {
          viewportStateStore.setSelectedContour(
            viewID.value,
            movingPointProbs.value.label,
            movingPointProbs.value.id,
          );
          viewportStateStore.setDrawingContour(
            viewID.value,
            movingPointProbs.value.label,
          );
        }
      }

      const startPoint = mousePathButtonDown.value[0];
      const endPoint
        = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
      const s = screenToImage(startPoint[0], startPoint[1]);
      const e = screenToImage(endPoint[0], endPoint[1]);
      const distancePoint = [e[0] - s[0], e[1] - s[1]];

      contourContainerStore.translateContour(
        curImageID.value,
        {
          label: movingPointProbs.value.label,
          id: movingPointProbs.value.id,
        },
        distancePoint,
      );
    }
  }
  stopDrawing();
}

function onMouseClick(payload) {
  // check for contour selection in other modes than contour drawing
  if (viewportStateStore.getToolMode(viewID.value) === ViewportToolMode.Contour)
    return;

  resetMouseStates();

  contourProbsButtonDown.value = isOverContourLine(payload);
  lineEndPointProbsButtonDown.value = isOverLineEndPoint(payload);
  centroidContourProbsButtonDown.value = isOverSelectedContourCentroid(payload);
  pointContourProbsButtonDown.value = isOverPointContour(payload);
  const textContourProbsButtonDown = isOverTextContour(payload);

  let selection = null;

  if (contourProbsButtonDown.value) {
    selection = contourProbsButtonDown.value;
  }

  // only line needs check for probs
  if (lineEndPointProbsButtonDown.value.properties) {
    selection = lineEndPointProbsButtonDown.value.properties;
  }

  if (centroidContourProbsButtonDown.value) {
    selection = centroidContourProbsButtonDown.value;
  }

  if (pointContourProbsButtonDown.value) {
    selection = pointContourProbsButtonDown.value;
  }
  if (textContourProbsButtonDown) {
    selection = textContourProbsButtonDown;
  }

  if (selection) {
    viewportStateStore.setSelectedContour(
      viewID.value,
      selection.label,
      selection.id,
    );
    viewportStateStore.setDrawingContour(viewID.value, selection.label);
    viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Contour);
  }

  resetMouseStates();
}

function onMouseDoubleClick(payload) {
  // if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour) return;

  if (!currentDrawingContourProps.value) {
    return;
  }
  // if (viewportStateStore.getToolMode(viewID.value) !== ViewportToolMode.Contour) return;
  if (
    viewportStateStore.getDrawingMode(viewID.value)
    !== ViewportDrawingMode.PointClick
  ) {
    return;
  }

  mousePathButtonDown.value.push(mouseEventToScreen(payload));

  // remove duplicate end points
  if (mousePathButtonDown.value.length > 5) {
    // Remove duplicate points at the end of array
    const lastPoint
      = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
    while (
      mousePathButtonDown.value.length
      && equalPoints(
        mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
        lastPoint,
      )
    ) {
      mousePathButtonDown.value.pop();
    }
    mousePathButtonDown.value.push(lastPoint);

    if (mousePathButtonDown.value.length < 3) {
      // abort drawing
      return;
    }

    const interpolator = new CurveInterpolator(mousePathButtonDown.value, {
      tension: 0,
      alpha: 0.7,
      closed: isClosedPolygonDrawing(),
    });
    const segments = 80;
    let pts = interpolator.getPoints(segments);

    pts = pts.map(point => screenToImage(point[0], point[1]));

    let id = encodeContourLabelItemId([-1]);
    if (selectedContour.value && selectedContour.value.label) {
      id = selectedContour.value.id;
    }

    contourContainerStore.addContour(
      curImageID.value,
      convertArrayToPointArray(pts),
      currentDrawingContourLabel.value,
      id,
    );
  }
  resetMouseStates();
  resetDrawingCanvas();
}

function stopPropagation(event) {
  if (event && event.stopPropagation) {
    event.stopPropagation();
  }
  if (event && event.preventDefault) {
    event.preventDefault();
  }
}

const mouseMoving = ref(false);
const mouseDown = ref(false);

onMounted(() => {
  showContextMenu.value = false;

  onEvent('mousedbclick', (payload) => {
    onMouseDoubleClick(payload);
    stopPropagation(payload);
  });
  onEvent('mouseclick', (payload) => {
    mouseDown.value = true;
    mouseMoving.value = false; // Reset mouse moving state

    closeContextMenu();
    onMouseClick(payload);
  });
  onEvent('contextmenu', (payload) => {
    if (!active.value) return;
    if (mouseMoving.value) {
      mouseMoving.value = false; // Reset mouse moving state
      return; // Ignore context menu if mouse is moving
    }
    initContextMenu(payload);
  });
  onEvent('mousedown', (payload) => {
    if (!active.value) return;
    mouseDown.value = true;
    mouseMoving.value = false; // Reset mouse moving state

    onMouseDown(payload);
  });

  onEvent('mousemove', (payload) => {
    if (!active.value) return;
    if (!mouseDown.value) return;
    mouseMoving.value = true;
    onMouseMove(payload);
  });

  onEvent('mouseup', (payload) => {
    onMouseUp(payload);
  });

  onEvent('mouseenter', (payload) => {
    //
  });

  onEvent('mouseleave', (payload) => {
    // resetMouseStates();
  });

  updateLeftCornerCoords();
});

// contour drawing
const overlay = ref(null);
const svg = ref(null);

function resetDrawingCanvas() {
  d3.select(overlay.value).selectAll('svg').remove();
  svg.value = d3
    .select(overlay.value)
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%');
}

function drawNudgeTool() {
  const group = createDrawGroup();
  const line = getLineGenerator();
  const strokeColor = currentDrawingContourProps.value.color;
  const center = screenToImage(
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][0],
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][1],
  );
  const nudgePolygonPts = nudgePolygon.value.map(point => [point.x, point.y]);

  // Create a unique ID for this gradient
  const gradientId = `nudgeToolGradient-${Date.now()}`;
  // Gradient lives in <defs> (referenced by id, not positioned).
  const gradient = svg.value
    .append('defs')
    .append('radialGradient')
    .attr('id', gradientId)
    .attr('cx', '50%')
    .attr('cy', '50%')
    .attr('r', '50%')
    .attr('fx', '50%')
    .attr('fy', '50%');

  // Add color stops to the gradient
  gradient
    .append('stop')
    .attr('offset', '0%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.5);

  gradient
    .append('stop')
    .attr('offset', '70%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.4);

  gradient
    .append('stop')
    .attr('offset', '100%')
    .attr('stop-color', strokeColor)
    .attr('stop-opacity', 0.3);

  // The nudge radius is in image px and scales with the image, so it is drawn
  // straight under the group matrix (no counter-scaling). Only the outline is
  // kept at a constant on-screen width via non-scaling-stroke.
  group
    .append('circle')
    .attr('cx', center[0])
    .attr('cy', center[1])
    .attr('r', nudgeRadius.value)
    .attr('fill', `url(#${gradientId})`)
    .attr('stroke', 'white')
    .attr('stroke-width', 1)
    .attr('vector-effect', 'non-scaling-stroke');

  group
    .append('path')
    .datum(nudgePolygonPts)
    .attr('fill', 'none')
    .attr('opacity', 1)
    .attr('stroke-linejoin', 'round') // Set line join style to round
    .attr('stroke-linecap', 'round')
    .attr('stroke', strokeColor)
    .attr('stroke-width', CONTOUR_STROKE)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('d', line);
}

function drawContourPath(resetCanvas = true) {
  const group = resetCanvas
    ? createDrawGroup()
    : svg.value.select('g.draw-root');

  const line = getLineGenerator();
  const strokeColor = currentDrawingContourProps.value.color;

  // Interpolation / hit-testing stays in screen space (unchanged behaviour);
  // points are converted to image space only when appended to the group matrix.
  const toImage = p => screenToImage(p[0], p[1]);
  const strokePath = (screenPts, color, opacity) => {
    group
      .append('path')
      .datum(screenPts.map(toImage))
      .attr('fill', 'none')
      .attr('opacity', opacity)
      .attr('stroke-linejoin', 'round') // Set line join style to round
      .attr('stroke-linecap', 'round')
      .attr('stroke', color)
      .attr('stroke-width', CONTOUR_STROKE)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', line);
  };

  let drawPath = mousePathButtonDown.value;

  if (
    viewportStateStore.getDrawingMode(viewID.value)
    === ViewportDrawingMode.PointClick
  ) {
    if (mousePathButtonDown.value.length < 2) {
      return;
    }
    const isClosedDrawing = isClosedPolygonDrawing();
    const interpolator = new CurveInterpolator(mousePathButtonDown.value, {
      tension: 0,
      alpha: 0.7,
      closed: isClosedDrawing,
    });
    const segments = 80;
    const pts = interpolator.getPoints(segments);
    drawPath = pts;

    if (isClosedDrawing) {
      const closestPointIdxLast = getClosestPointArrayIdx(
        mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
        drawPath,
      );

      // Split drawPath at the closest point
      const firstPathSegment = drawPath.slice(0, closestPointIdxLast + 1);
      const secondPathSegment = drawPath.slice(closestPointIdxLast);

      strokePath(firstPathSegment, strokeColor, 1);
      strokePath(secondPathSegment, 'white', 0.15);

      return;
    }
  }

  strokePath(drawPath, strokeColor, 1);
}

function drawContourPathThreshold() {
  const group = createDrawGroup();
  const line = getLineGenerator();

  if (!mousePathButtonDownThreshold.value.length) {
    return;
  }
  let strokeColor = currentDrawingContourProps.value.color;
  const innerContourProbs = getThresholdInnerContourProbs();

  // Threshold contours are already in image-pixel space, so they are drawn
  // straight into the group matrix (no imageToScreen conversion needed).
  const strokePath = (pts, color) => {
    group
      .append('path')
      .datum(pts)
      .attr('fill', 'none')
      .attr('opacity', 1)
      .attr('stroke-linejoin', 'round') // Set line join style to round
      .attr('stroke-linecap', 'round')
      .attr('stroke', color)
      .attr('stroke-width', CONTOUR_STROKE)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', line);
  };

  // first is outer, rest are inner

  for (const [idx, contour] of mousePathButtonDownThreshold.value.entries()) {
    if (contour.length < 3) {
      // if outer contour not valid break
      if (idx === 0) {
        return;
      }

      continue;
    }

    if (idx > 0 && !innerContourProbs) {
      return;
    }

    if (idx > 0) {
      strokeColor = innerContourProbs.color;
    }

    strokePath(contour, strokeColor);
  }
}

function drawEndpointMovePreview() {
  if (
    currentDrawingContourProps.value?.type
    === ContourDefinitions.ContourType.AnnotationArrowContour
  ) {
    const staticIdx = lineEndPointProbsButtonDown.value.staticPointIndex;
    const movingIdx = 1 - staticIdx;
    const pts = [];
    pts[staticIdx] = mousePathButtonDown.value[0];
    pts[movingIdx]
      = mousePathButtonDown.value[mousePathButtonDown.value.length - 1];
    drawArrow(pts[0], pts[1]); // arrowhead stays at index 1 (the head)
  }
  else {
    drawLine();
  }
}

// Live-drawing group in image-pixel space. It carries the image->screen matrix,
// so draws made with the shared primitives match the committed contours drawn by
// UIDDisplayContours (same sizes, rotation and non-scaling strokes).
function createDrawGroup() {
  resetDrawingCanvas();
  return svg.value
    .append('g')
    .attr('class', 'draw-root')
    .attr('transform', overlayTransform.value);
}

// Counter-scales circle radii / font sizes and keeps text upright, mirroring the
// display overlay. Call once after all primitives are appended to the group.
function finalizeDrawGroup(group) {
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

function drawArrow(
  startPoint = mousePathButtonDown.value[0],
  endPoint = mousePathButtonDown.value[mousePathButtonDown.value.length - 1],
) {
  const strokeColor = currentDrawingContourProps.value.color;
  const group = createDrawGroup();

  const start = screenToImage(startPoint[0], startPoint[1]);
  const end = screenToImage(endPoint[0], endPoint[1]);

  // Same shape helper as the committed arrow in UIDDisplayContours.
  appendArrow(group, start, end, strokeColor, 1);
}

function drawLine() {
  const strokeColor = currentDrawingContourProps.value.color;
  const group = createDrawGroup();

  const start = screenToImage(
    mousePathButtonDown.value[0][0],
    mousePathButtonDown.value[0][1],
  );
  const end = screenToImage(
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][0],
    mousePathButtonDown.value[mousePathButtonDown.value.length - 1][1],
  );
  const line = [start, end];

  appendPath(group, line, strokeColor, 1, CONTOUR_STROKE);

  appendCircle(group, start[0], start[1], ENDPOINT_RADIUS, { fill: strokeColor });
  appendCircle(group, end[0], end[1], ENDPOINT_RADIUS, { fill: strokeColor });

  // Drawn under the group matrix, so the crossbar offset stays in image px
  // (no `linear` argument) — same as the committed line in UIDDisplayContours.
  if (
    currentDrawingContourProps.value?.label
    === ContourDefinitions.ContourLabel.LaxLvRange
  ) {
    drawRangeCrossbars(group, line, localizerDir2D.value, {
      color: strokeColor,
      opacity: RANGE_CROSS_OPACITY,
      strokeWidth: RANGE_CROSS_STROKE,
      nonScalingStroke: true,
    });
  }

  finalizeDrawGroup(group);
}

// Re-applies the image->screen matrix to a live drawing if the zoom changes
// while it is on screen. Draws now live under a group matrix with
// non-scaling-strokes, so this just refreshes the group transform + handle
// sizes (mirrors UIDDisplayContours).
function applyTransform() {
  const group = d3.select(overlay.value).select('g.draw-root');
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

watch(
  () => scaleFactor.value,
  () => {
    requestAnimationFrame(applyTransform);
  },
  { immediate: true },
);
</script>

<!-- <script setup lang="ts"></script> -->
<script>
export default {
  name: 'UIDAdjustContours',
};
</script>

<template>
  <div ref="overlayContainer" class="overlay">
    <div
      ref="overlay"
      style="will-change: transform"
      :style="{
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
      }"
    />
  </div>
  <div
    v-show="annotationInputVisible"
    class="pie-menu-container-center"
    @mousedown.stop.prevent="forceAnnotationCommit"
  >
    <TextAnnotationInput
      ref="annotationInputRef"
      :visible="annotationInputVisible"
      :annotation-input-position="annotationInputPosition"
      :existing-text="annotationInputValue"
      @commit="commitAnnotationInput"
      @cancel="cancelAnnotationInput"
    />
  </div>
  <div v-if="showContextMenu" class="pie-menu-container-center">
    <PieMenu
      :view-id="viewID"
      :current-image-u-i-d="curImageID"
      :click-x="contextMenuCoords[0]"
      :click-y="contextMenuCoords[1]"
      @close="closeContextMenu"
    />
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 100%;
  height: 100%;
}

.pie-menu-container-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  z-index: 1000;
  pointer-events: all;
}

.pie-menu-container-center :deep(.pie-menu) {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: all;
}
</style>
